import React, { useState, useMemo } from "react";
import "./InvestmentCalc.css";
import swipeSwipeImage from "../../../assets/swipeswipe_logo.png";
// import BigNumber from "bignumber.js";

import {
  Radio,
  RadioGroup,
  FormControlLabel,
  Slider,
  Typography,
  Box,
  Container,
  Grid,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Rectangle,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { styled } from "@mui/system";

import SwipeNumberField from "../../atom/SwipeNumberField";
import CustomGraphTooltip from "../../atom/CustomGraphTooltip";

const CustomSlider = styled(Slider)({
  maxWidth: 313,
  height: 8,
  color: "#2c2c6e", // Color for the track
  "& .MuiSlider-thumb": {
    height: 30,
    width: 30,
    backgroundImage: `url(${swipeSwipeImage})`,
    backgroundSize: "cover",
    border: "none",
    "&:focus, &:hover, &.Mui-active": {
      boxShadow: "inherit",
    },
  },
  "& .MuiSlider-track": {
    border: "none",
    backgroundColor: "#2c2c6e",
  },
  "& .MuiSlider-rail": {
    opacity: 1,
    backgroundColor: "#E6E6E6",
  },
});

const InvestmentCalculator = () => {
  const [initialDeposit, setInitialDeposit] = useState("100");
  const [contributions, setContributions] = useState("200");
  const [contributionFrequency, setContributionFrequency] = useState("weekly");
  const [yearsToGrow, setYearsToGrow] = useState(30);
  const [annualReturn, setAnnualReturn] = useState(10);
  const BigNumber = require("bignumber.js");

  // useEffect(() => {
  BigNumber.config({ DECIMAL_PLACES: 100, ERRORS: false });
  //   // on the first load, set the error boundary for BigNumber
  // }, []);

  let adjustedContributions =
      contributions === "" ? 0 : parsePlaceholderNumber(contributions),
    adjustedInitialDeposit =
      initialDeposit === "" ? 0 : parsePlaceholderNumber(initialDeposit),
    adjustedAnnualReturn =
      annualReturn === "" ? 0 : parsePlaceholderNumber(annualReturn);

  const calculateFutureBalance = () => {
    let n;
    switch (contributionFrequency) {
      case "daily":
        n = 365;
        break;
      case "weekly":
        n = 52;
        break;
      case "monthly":
        n = 12;
        break;
      case "yearly":
        n = 1;
        break;
      default:
      // throw new Error(
      //   'Invalid compounding frequency. Choose from "daily", "weekly", "monthly", or "yearly".'
      // );
    }
    const r = adjustedAnnualReturn / 100 / n;
    const t = yearsToGrow * n;
    let futureBalance = adjustedInitialDeposit * Math.pow(1 + r, t);
    for (let i = 1; i <= t; i++) {
      futureBalance += adjustedContributions * Math.pow(1 + r, t - i);
    }

    // function formatLargeNumber(number) {
    //   const num = new BigNumber(number);

    //   if (num.isGreaterThanOrEqualTo(new BigNumber("1e12"))) {
    //     return num.dividedBy(new BigNumber("1e12")).toFixed(2) + "T";
    //   } else if (num.isGreaterThanOrEqualTo(new BigNumber("1e9"))) {
    //     return num.dividedBy(new BigNumber("1e9")).toFixed(2) + "B";
    //   } else if (num.isGreaterThanOrEqualTo(new BigNumber("1e6"))) {
    //     return num.dividedBy(new BigNumber("1e6")).toFixed(2) + "M";
    //   } else if (num.isGreaterThanOrEqualTo(new BigNumber("1e3"))) {
    //     return num.dividedBy(new BigNumber("1e3")).toFixed(2) + "K";
    //   } else {
    //     return num.toString();
    //   }
    // }

    // formatLargeNumber(futureBalance);

    return Math.floor(futureBalance);
  };

  const formatPlaceholderNumber = (num) => {
    if (!num) return "";
    const parts = num.toString().split(".");
    parts[0] = parts[0].split(",").join("");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  // function to parse and return a numeric value of the comma seperated placeholder value
  function parsePlaceholderNumber(formattedNum) {
    if (typeof formattedNum === "number") return formattedNum;
    else if (!formattedNum) return 0;

    return parseFloat(formattedNum.replace(/,/g, ""));
  }

  const chartData = useMemo(() => {
    const rate = annualReturn / 100;
    const contributionNumber = parsePlaceholderNumber(contributions);
    const initialDepositNumber = parsePlaceholderNumber(initialDeposit);

    let n;
    switch (contributionFrequency) {
      case "daily":
        n = 365;
        break;
      case "weekly":
        n = 52;
        break;
      case "monthly":
        n = 12;
        break;
      case "yearly":
        n = 1;
        break;
      default:
      // throw new Error(
      //   'Invalid compounding frequency. Choose from "daily", "weekly", "monthly", or "yearly".'
      // );
    }

    const yearlyAmounts = [];
    let totalAmount = initialDepositNumber;
    let previousAmount = initialDepositNumber;
    let principalAndContributions = initialDepositNumber;
    const currentYear = new Date().getFullYear();
    let interestAcrrued = 0;

    for (let year = 1; year <= yearsToGrow; year++) {
      for (let i = 1; i <= n; i++) {
        totalAmount = totalAmount * (1 + rate / n) + contributionNumber;
      }

      // add to the contributions to the principalAndContributions
      principalAndContributions += n * contributionNumber;

      // FIXME: interest is not working correctly.
      const interestEarned = Number(
        totalAmount - previousAmount - contributionNumber * n
      );
      interestAcrrued += interestEarned;

      yearlyAmounts.push({
        year: Number(currentYear) + year,
        amount: Math.floor(totalAmount),
        // investment and return are the 2 fields to be used in the chart
        // Return: Math.floor(interestEarned),
        Return: Math.floor(interestAcrrued),
        // Return: interestEarned,
        Investment: principalAndContributions,
      });
      previousAmount = totalAmount;
    }
    return yearlyAmounts;
  }, [
    initialDeposit,
    contributions,
    contributionFrequency,
    yearsToGrow,
    annualReturn,
  ]);

  const handleInitialDepositChange = (e) => {
    if (parsePlaceholderNumber(e.target.value) <= 999999) {
      setInitialDeposit(formatPlaceholderNumber(e.target.value));
    }
  };
  const handleAnnualReturn = (e) => {
    if (Number(e.target.value) < 100) {
      setAnnualReturn(e.target.value);
    }
  };
  const handleContributionChange = (e) => {
    if (parsePlaceholderNumber(e.target.value) <= 99999) {
      setContributions(formatPlaceholderNumber(e.target.value));
    }
  };

    const currencyFormatter = (min = 2, max = 2) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: min,
        minimumFractionDigits: max,
    });


  const formatAxisText = (value) => {
    if (value >= 1e6) {
      return currencyFormatter(1, 1).format(value / 1e6) + "M";
    } else if (value >= 1e3) {
      return currencyFormatter(1, 1).format(value / 1e3) + "k";
    }
    return value.toString();
  };

  return (
    <Container className="main-box">
      { window === window.parent && 
      <Typography
      className="text-centre main-heading"
      variant="h3"
      sx={{
        display: "flex",
        justifyContent: "center",
        fontFamily: "Caudex, Arial, sans-serif",
        fontWeight: 700,
        color: "#293a60",
      }}
      gutterBottom
      textAlign={'center'}
      paddingTop={5} 
      paddingBottom={5}
    >
      Savings & Investment Calculator
    </Typography>} 

      {/* content start */}
      <Grid
      container
      columns={16}
      columnSpacing={{xs: 2, md: 2}}
      >
        {/* left section */}
        <Grid
            xs={16}
            md={4}
            item
        >
            <Grid className="left-stack">
          <Typography className="capital heading-text">
            Initial Investment Deposit
          </Typography>
          <SwipeNumberField
            className="field-input"
            prefix="$"
            value={initialDeposit}
            onChange={(e) => handleInitialDepositChange(e)}
            margin="normal"
            placeholder="0"
          />
          <Typography marginTop={'25px'} className="capital heading-text">
            Contributions
          </Typography>

          <SwipeNumberField
            className="field-input"
            prefix="$"
            value={contributions}
            onChange={(e) => handleContributionChange(e)}
            margin="normal"
            placeholder="0"
          />
          <RadioGroup
            value={contributionFrequency}
            onChange={(e) => setContributionFrequency(e.target.value)}
            row
            className="radio-group"
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gridTemplateRows: "repeat(2, 1fr)",
                marginTop: '25px',
                color: "#293a60"
              }}
            >
              <FormControlLabel
                value="yearly"
                control={<Radio sx={{color: 'gray.main'}} />}
                label="Annual"
              />
              <FormControlLabel
                value="monthly"
                control={<Radio sx={{color: 'gray.main'}} />}
                label="Monthly"
              />
              <FormControlLabel
                value="weekly"
                control={<Radio sx={{color: 'gray.main'}} />}
                label="Weekly"
              />
              <FormControlLabel
                value="daily"
                control={<Radio sx={{color: 'gray.main'}} />}
                label="Daily"
              />
            </Box>
          </RadioGroup>
          <Typography marginTop={'20px'} marginBottom={'30px'} justifyContent={'center'} gutterBottom className="heading-text capital">
            {yearsToGrow} Years to Grow
          </Typography>

          <CustomSlider
            value={yearsToGrow}
            onChange={(e, newValue) => setYearsToGrow(newValue)}
            valueLabelDisplay="auto"
            step={1}
            min={1}
            max={50}
            size="small"
            color="secondary"
          />
          <Typography marginTop={'25px'} className="capital heading-text">
            Average Annual Return
          </Typography>

          <SwipeNumberField
            // symbol
            className="field-input"
            type="text"
            maxLength="2"
            suffix="%"
            min={0}
            max={99}
            defaultValue={10}
            value={annualReturn}
            onChange={(e) => handleAnnualReturn(e)}
            margin="normal"
          />
          </Grid>
        </Grid>

        {/* right section */}
        <Grid item xs={16} md={12} >
            <Grid className="right-stack">
          <Box>
            <Typography
              variant="h6"
              className="capital heading-text text-center"
              gutterBottom
            >
              Potential Future Balance in {yearsToGrow} years
            </Typography>
            <Typography
              variant="h3"
              className="capital text-center heading-text"
              gutterBottom
              fontWeight="bold"
            >
              ${calculateFutureBalance().toLocaleString("en-US")}
            </Typography>
          </Box>
          <ResponsiveContainer width="100%" minWidth={300} minHeight={450} height="80%">
            <BarChart data={chartData}>
                <XAxis dataKey="year" stroke="#949EAB" />
                <YAxis tickFormatter={formatAxisText}  stroke="#949EAB" />
                <Tooltip cursor={{fill:'#E6E6E6'}} content={<CustomGraphTooltip chartData={chartData}  />} />
                <CartesianGrid strokeWidth="1" vertical={false} />
                <Legend />
                <Bar
                dataKey="Investment"
                label="Investment"
                stackId="a"
                legendType="circle"
                fill="#293A60"
                activeBar={<Rectangle fill="#23A6F0" />}
                />
                <Bar
                dataKey="Return"
                legendType="circle"
                stackId="a"
                fill="#FBC950"
                activeBar={<Rectangle fill="#23A6F0" />}
                />
            </BarChart>
          </ResponsiveContainer>
          </Grid>
        </Grid>
      </Grid>

      {/* end of the left stack */}
    </Container>
  );
};

export default InvestmentCalculator;
